import React from "react";
import { Link } from "react-router-dom";
import "../../styles/verifier.css";

interface VerifierInfo {
    generationStatus?: string;
    title?: string;
    createdBy?: string;
    quoteHelp?: number;
    currentStreakNum?: number;
    nextAOTDEmojis?: string[];
    thisAOTDEmojis?: string[];
    hasPermission?: boolean;
    views?: {
        total?: number;
        verified?: number;
    };
    generationTime?: number;
    tweetUrl?: string;
    waybackMachineUrl?: string;
    waybackMachineCapturedAt?: string;
    waybackMachineTimestamp?: string;
    previousWaybackMachineUrl?: string;
    waybackMachineStatus?: 'pending' | 'completed' | 'failed';
    waybackMachineRequestedAt?: string;
    waybackMachineLastAttemptAt?: string;
    waybackMachineError?: string;
    canTriggerWaybackMachine?: boolean;
}

interface VerifierProps {
    info?: VerifierInfo;
    onTriggerWayback?: () => void;
}

const Verifier: React.FC<VerifierProps> = ({ info }) => {
    if (!info) {
        return null;
    }

    const {
        waybackMachineUrl,
        waybackMachineStatus,
        waybackMachineRequestedAt,
    } = info;

    // determine wayback status
    const getWaybackStatus = (): 'completed' | 'pending' | 'failed' | 'missing' => {
        if (waybackMachineUrl) {
            return 'completed';
        }
        if (waybackMachineStatus === 'pending') {
            return 'pending';
        }
        if (waybackMachineStatus === 'failed') {
            return 'failed';
        }
        // check if requestedAt is within 10 minutes (pending window)
        if (waybackMachineRequestedAt) {
            const requestedAt = new Date(waybackMachineRequestedAt);
            const now = new Date();
            const elapsed = now.getTime() - requestedAt.getTime();
            const TEN_MINUTES_MS = 10 * 60 * 1000;
            if (elapsed < TEN_MINUTES_MS) {
                return 'pending';
            }
        }
        return 'missing';
    };

    const waybackStatus = getWaybackStatus();

    const waybackArchiveUrl = "https://web.archive.org/web/*/https://aotd.n450s.com";

    return (
        <div className="verifier-container">
            {/* compact row: icon + label + share link */}
            <div className="verifier-header">
                <div className="verifier-icon-wrapper">
                    <svg
                        className="verifier-wayback-icon"
                        viewBox="0 0 50.8 50.8"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M11.377 34.396V18.58m9.349 15.816V18.58m9.348 15.816V18.58m9.349 15.816V18.58m1.57-5.29H9.807m-.092 26.397h31.37" style={{ fill: "none", stroke: "#000000", strokeWidth: 3.175, strokeLinecap: "round", strokeLinejoin: "miter", strokeDasharray: "none", strokeOpacity: 1 }} />
                        <path d="M160.724 172.373H31.276" style={{ fill: "none", stroke: "#000000", strokeWidth: 12, strokeLinecap: "round", strokeLinejoin: "miter", strokeDasharray: "none", strokeOpacity: 1 }} transform="translate(0 -.628) scale(.26458)" />
                        <path d="M8.28 8.791h34.24L25.4 5.027Z" style={{ fill: "#000000", fillOpacity: 1, stroke: "#000000", strokeWidth: 1.5875, strokeLinecap: "round", strokeLinejoin: "round", strokeDasharray: "none", strokeOpacity: 1 }} />
                    </svg>
                </div>
                <span className="verifier-label text-xs text-gray-400">
                    wayback machine {waybackStatus === 'completed' ? <span>verified</span> : waybackStatus === 'pending' ? <span style={{ color: '#ffc107' }}>pending</span> : waybackStatus === 'failed' ? <span style={{ color: '#dc3545' }}>failed</span> : 'not captured'}
                    {' and '}
                    <Link to="/art-of-the-day/termsandownership" className="verifier-link">terms</Link>
                </span>
                <a
                    href={waybackArchiveUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="verifier-share-link"
                    title="View on Wayback Machine"
                    style={{ marginLeft: '6px', color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

export default Verifier;
